import { useState } from 'react';
import { Box } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import {
    useTranslate,
    DashboardMenuItem,
    MenuItemLink,
    MenuProps,
    useSidebarState,
} from 'react-admin';
import clsx from 'clsx';
import SubMenu from './SubMenu';
import Configuration from '../configuration';
import Flows from '../flows';
import Transaction from '../transaction';
import Administration from '../administration';
import Roles from '../roles';
import User from '../user';
import Contractor from '../contractor';
import TransactionHistory from '../transactionHistory';
import Promotions from '../promotions';

type MenuName = 'menuUsers' | 'menuSales' | 'menuCustomers' | 'menuAdministration' | 'menuConfiguration' | 'menuFlows';

const Menu = ({ dense = false }: MenuProps) => {
    const [state, setState] = useState({
        menuUsers: true,
        menuSales: true,
        menuCustomers: true,
        menuAdministration: true,
        menuConfiguration: true,
        menuFlows: true
    });
    const translate = useTranslate();
    const [open] = useSidebarState();

    const handleToggle = (menu: MenuName) => {
        setState(state => ({ ...state, [menu]: !state[menu] }));
    };

    const menuItemStyle = {
        color: 'rgba(0,0,0,0.6)',
        '& .MuiListItemIcon-root': {
            color: 'rgba(0,0,0,0.6)'
        },
        borderRadius: '15px',
        '&:hover': {
            backgroundColor: '#5a7ff5', 
        },
        '&.RaMenuItemLink-active': {
            backgroundColor: '#3965f0',
            color: 'white', 
            borderLeft: 'none', 
            '& .MuiListItemIcon-root': {
                color: 'white'
            },
        },
    };

    return (
        <Box
            sx={{
                width: open ? 300 : 50,
                marginTop: 1,
                marginBottom: 1,
                transition: theme =>
                    theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
            }}
            className={clsx({
                'RaMenu-open': open,
                'RaMenu-closed': !open,
            })}
        >
            <DashboardMenuItem sx={menuItemStyle}/>
            <SubMenu
                handleToggle={() => handleToggle('menuAdministration')}
                isOpen={state.menuAdministration}
                name="pos.menu.admin"
                dense={dense}
            >
                <MenuItemLink
                    to="/administration"
                    state={{ _scrollToTop: true }}
                    primaryText={translate(`resources.administratorManagement.name`, { smart_count: 2 })}
                    leftIcon={<Administration.icon/>}
                    dense={dense}
                    sx = {menuItemStyle}
                />
                <MenuItemLink
                    to="/roles"
                    state={{ _scrollToTop: true }}
                    primaryText={translate(`resources.roleAdministrator.name`, { smart_count: 2 })}
                    leftIcon={<Roles.icon />}
                    dense={dense}
                    sx = {menuItemStyle}
                />
            </SubMenu>
            <MenuItemLink
                to="/configuration"
                state={{ _scrollToTop: true }}
                primaryText={translate(`resources.configuration.name`, { smart_count: 2 })}
                leftIcon={<Configuration.icon />}
                dense={dense}
                sx = {menuItemStyle}
            />
            <MenuItemLink
                to="/flows"
                state={{ _scrollToTop: true }}
                primaryText={translate(`resources.flows.name`, { smart_count: 2 })}
                leftIcon={<Flows.icon />}
                dense={dense}
                sx = {menuItemStyle}
            />
            <SubMenu
                handleToggle={() => handleToggle('menuUsers')}
                isOpen={state.menuUsers}
                name="pos.menu.users"
                dense={dense}
            >
                <MenuItemLink
                    to="/users"
                    state={{ _scrollToTop: true }}
                    primaryText={translate(`resources.userManagement.name`, { smart_count: 2 })}
                    leftIcon={<User.icon/>}
                    dense={dense}
                    sx = {menuItemStyle}
                />
                <MenuItemLink
                    to="/contractor"
                    state={{ _scrollToTop: true }}
                    primaryText={translate(`resources.verifyContractor.name`, { smart_count: 2 })}
                    leftIcon={<Contractor.icon />}
                    dense={dense}
                    sx = {menuItemStyle}
                />
                <MenuItemLink
                    to="/transactions"
                    state={{ _scrollToTop: true }}
                    primaryText={translate(`resources.transactions.name`, { smart_count: 2 })}
                    leftIcon={<Transaction.icon />}
                    dense={dense}
                    sx = {menuItemStyle}
                />
            </SubMenu>
            <MenuItemLink
                to="/orders"
                state={{ _scrollToTop: true }}
                primaryText={translate(`resources.orders.name`, { smart_count: 2 })}
                leftIcon={<DescriptionIcon />}
                dense={dense}
                sx = {menuItemStyle}
            />
            <MenuItemLink
                to="/transaction-history"
                state={{ _scrollToTop: true }}
                primaryText={translate(`resources.transactionHistory.name`, { smart_count: 2 })}
                leftIcon={<TransactionHistory.icon />}
                dense={dense}
                sx = {menuItemStyle}
            />
            <MenuItemLink
                to="/promotions"
                state={{ _scrollToTop: true }}
                primaryText={translate(`resources.promotions.name`, { smart_count: 2 })}
                leftIcon={<Promotions.icon />}
                dense={dense}
                sx = {menuItemStyle}
            />
        </Box>
    );
};

export default Menu;